// Vista de observabilidad del outbox de Vimos Digital (Panel Super Admin).
//
// Solo lectura: no toca filas del outbox ni el worker — solo detecta y
// visualiza estados anómalos (pendientes acumulados, sending atascadas,
// alertas y fallidas) para que un problema de sincronización con el
// directorio sea visible sin alterar el procesamiento.

import { crearTabla } from '@plataforma/ui';

import { obtenerVimosOutboxResumen } from '../api/backoffice.js';
import { exigirSuperAdmin } from '../guardas.js';

import {
  crearBadge,
  estadoCarga,
  estadoError,
  fechaHora,
  tarjetaMetrica,
} from './backoffice-ui.js';
import { montarShell } from './shell.js';

const ESTADOS_OUTBOX = {
  pending: { etiqueta: 'Pendiente', tono: 'alerta' },
  sending: { etiqueta: 'Enviando', tono: 'info' },
  sent: { etiqueta: 'Enviado', tono: 'exito' },
  alert: { etiqueta: 'Alerta', tono: 'peligro' },
  failed: { etiqueta: 'Fallido', tono: 'peligro' },
};

export async function vistaVimosOutbox(contenedorRaiz) {
  if (!exigirSuperAdmin()) return;

  const contenido = montarShell(contenedorRaiz, '/vimos-outbox');
  contenido.innerHTML = `
    <div class="pf-backoffice-header">
      <div>
        <h1>Vimos Digital · Outbox</h1>
        <p class="pf-texto-suave">
          Salud de la sincronización de tiendas con el directorio (vista de solo lectura).
        </p>
      </div>
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-refrescar-vimos">Refrescar</button>
    </div>
    <div id="pf-outbox-vimo">${estadoCarga('Consultando outbox de Vimos...')}</div>
  `;

  contenido.querySelector('#pf-btn-refrescar-vimos').addEventListener('click', () => {
    cargar(contenido);
  });

  await cargar(contenido);
}

async function cargar(contenido) {
  const destino = contenido.querySelector('#pf-outbox-vimo');
  destino.innerHTML = estadoCarga('Consultando outbox de Vimos...');

  const { data, error } = await obtenerVimosOutboxResumen();
  if (error || !data) {
    destino.innerHTML = estadoError('No se pudo consultar el estado del outbox de Vimos.');
    return;
  }

  const conteos = data.conteos ?? {};
  const flujos = data.flujos ?? {};
  const anomalias = data.anomalias ?? {};
  const detalle = data.detalle ?? [];

  const hayAnomalia =
    anomalias.pendientes_acumulados ||
    anomalias.sending_atascadas ||
    anomalias.alertas ||
    anomalias.fallidas;

  const mensajesAnomalia = [
    anomalias.pendientes_acumulados &&
      'Eventos pendientes acumulados que el worker no está drenando (revisar el cron y la Edge Function).',
    anomalias.sending_atascadas &&
      'Cambios en «enviando» con más de 15 minutos: probable worker detenido o con timeout.',
    anomalias.alertas &&
      'Eventos en «alerta»: Vimos rechazó firma/payload (posible secreto o reloj).',
    anomalias.fallidas &&
      'Eventos «fallidos»: agotaron sus 5 reintentos y requieren revisión manual.',
  ].filter(Boolean);

  destino.innerHTML = `
    <div class="pf-dashboard__tarjetas">
      ${tarjetaMetrica({
        titulo: 'Pendientes por enviar',
        valor: flujos.pending_listas ?? 0,
        subtitulo: `${conteos.pending ?? 0} pendientes · ${flujos.pending_diferidas ?? 0} en backoff`,
        tono: (flujos.pending_listas ?? 0) > 0 ? 'peligro' : 'exito',
      })}
      ${tarjetaMetrica({
        titulo: '«Enviando» atascadas',
        valor: flujos.sending_atascadas ?? 0,
        subtitulo: `${conteos.sending ?? 0} enviando ahora`,
        tono: (flujos.sending_atascadas ?? 0) > 0 ? 'peligro' : 'info',
      })}
      ${tarjetaMetrica({
        titulo: 'Alertas',
        valor: conteos.alert ?? 0,
        subtitulo: 'No reintentables (firma/payload)',
        tono: (conteos.alert ?? 0) > 0 ? 'alerta' : 'exito',
      })}
      ${tarjetaMetrica({
        titulo: 'Fallidas',
        valor: conteos.failed ?? 0,
        subtitulo: 'Agotaron reintentos',
        tono: (conteos.failed ?? 0) > 0 ? 'peligro' : 'exito',
      })}
      ${tarjetaMetrica({
        titulo: 'Enviadas (24 h)',
        valor: flujos.sent_24h ?? 0,
        subtitulo: `${conteos.sent ?? 0} enviadas en total`,
        tono: 'info',
      })}
      ${tarjetaMetrica({
        titulo: 'Total en cola',
        valor: conteos.total ?? 0,
        subtitulo: data.ultima_actividad
          ? `Última actividad: ${fechaHora(data.ultima_actividad)}`
          : 'Sin actividad registrada',
        tono: 'neutral',
      })}
    </div>

    ${
      hayAnomalia
        ? `
      <div class="pf-alerta pf-alerta--peligro" style="margin-top: 16px;">
        <div class="pf-alerta__icono"></div>
        <div>
          <p class="pf-alerta__titulo">Anomalías detectadas en la sincronización con Vimos</p>
          <ul style="margin: 0; padding-left: 18px;">
            ${mensajesAnomalia.map((m) => `<li>${m}</li>`).join('')}
          </ul>
        </div>
      </div>`
        : `
      <div class="pf-alerta pf-alerta--exito" style="margin-top: 16px;">
        <div class="pf-alerta__icono"></div>
        <div>
          <p class="pf-alerta__titulo">Sin anomalías</p>
          <p>El outbox de Vimos fluye con normalidad.</p>
        </div>
      </div>`
    }

    <section class="pf-tarjeta pf-backoffice-card" style="margin-top: 20px;">
      <h2>Eventos a revisar</h2>
      <div id="pf-outbox-tabla" style="margin-top: 12px;"></div>
    </section>
  `;

  const destinoTabla = destino.querySelector('#pf-outbox-tabla');
  destinoTabla.appendChild(
    crearTabla({
      filaVacia: 'No hay eventos pendientes, atascados, en alerta ni fallidos.',
      columnas: [
        { titulo: 'Actualizado', render: (fila) => fechaHora(fila.actualizado_en) },
        { titulo: 'Evento', render: (fila) => `${fila.event_type} · ${fila.event_id}` },
        { titulo: 'Estado', render: (fila) => crearBadge(ESTADOS_OUTBOX, fila.status) },
        {
          titulo: 'Empresa',
          render: (fila) => fila.empresa_nombre ?? (fila.empresa_id ? '—' : '(eliminada)'),
        },
        { titulo: 'Intentos', render: (fila) => String(fila.attempts ?? 0) },
        { titulo: 'Próx. intento', render: (fila) => fechaHora(fila.next_retry_at) },
        { titulo: 'Último error', render: (fila) => fila.last_error ?? '—' },
      ],
      filas: detalle,
    }),
  );
}
